import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { lionSelectors } from '@rosetta/react-lion';
import { ACCOUNT_TYPE, authActions, authSelectors, LOGIN_STATUS } from '@rosetta/react-auth';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { appActions, appSelectors } from '../../app/reducer/app-reducer';
import { passwordChangeActions } from '../reducer/password-change-reducer';
import { verticalLine } from '../../../styles/colors';
import { parse } from 'query-string';
import { ssoLoginActions } from '../reducer/sso-login-reducer';
import { LaunchpadButtonNew, WorkspaceLink, Link } from '../../../styles/LaunchpadButton';
import { getSupportLocale } from '../../app/services/language-helpers';
import { validateUserInfo } from '../../app/util/user-helper';
import { HelpIconContainer as HelpIcon } from '../../app/util/HelpIcon';
import { launchAppWithKey } from '../../../helpers/productConfigHelper';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { LoginContainer as LoginDiv } from './LoginContainer'
import { TextInput } from '../../app/util/TextInput';
import { breakpoints } from '../../../styles/constants'
import { MultipleAccountsContainer } from '../../app/components/MultipleAccountsContainer';
import { isMobile as isMobileDetected } from 'react-device-detect';

const MIN_PASSWORD_LENGTH = 1;
const LAST_WORKSPACE_STORAGE = 'lastLoginWorkspace';

const Form = styled.form`
  display: flex;
  align-items: center;
  flex-direction: column;
  & > *:not(:last-child) {
    margin-bottom: 20px;
  };
`

/* includes sign in help link, and forgot password link */
const SignInHelpContainer = styled.div(props => `
  display: flex;
  justify-content: center;
  font-size: 14px;
  /* if it's school/business view, add margin under sign in button */
  margin: ${props.showSchoolBusinessLogin ? '20px auto 0' : 'auto'};
  & > span {
    width: 74px;
  }
  a {
    font-size: 14px;
    &:first-of-type {
      margin-right: 8px;
      position: relative;
      &:after {
        content: '';
        position: absolute;
        width: 1px;
        height: 75%;
        ${!props.longLanguage && (`background: ${verticalLine}`)};
        transform: translateY(25%);
        right: -8px;
      }
    }
    &:last-of-type {
      margin-left: 8px;
    }
  }
  @media only screen and (max-width: 670px) {
    background: #salmon;
    flex-direction: column;
    & > div:first-of-type {
      margin-bottom: 15px;
    }
  }
`)

const RememberMeCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  input {
    cursor: pointer;
  }
`

const RememberMeLabel = styled.label(() => {
  let style = {
    //border: '1px solid red',
    fontSize: '15px',
    fontWeight: '500',
    lineHeight: '20.43px',
    paddingLeft: '4px',
    cursor: 'pointer',
    '& a': {
      color: "black",
      textDecoration: 'none',
      ':hover': {
        textDecoration: 'underline'
      }
    }
  };
  return style;
});

const P = styled.p`
  padding-bottom: 6px;
  margin-top: -5px;
  text-align: left;
  ${breakpoints.SCREEN_XS} {
    text-align: center;
  }
`

const setLocalStorageLastLoginWorkspace = (namespace) => {
  if (namespace === undefined) {
    namespace = null;
  }
  localStorage.setItem(LAST_WORKSPACE_STORAGE, JSON.stringify({workspace: namespace, createdTime: Date.now()}));
}

const getLocalStorageLastLoginWorkspace = () => {
  const item = localStorage.getItem(LAST_WORKSPACE_STORAGE);
  if (!item) {
    return null;
  }
  const lastLoginWorkspace = JSON.parse(item);
  if (!lastLoginWorkspace || lastLoginWorkspace.workspace == null) {
    return null;
  }
  return lastLoginWorkspace.workspace;
}



//*********************************************
export class Login extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showLogin: true,
      validatingEmail: false,
      tempPassword: '',
      tempUsername: '',
      tempWorkspace: '',
      tempAccountType: '',
      redirectParams: '',
      redirecting: false,
      contentPath: '',
      rememberMeChecked: false,
      showSchoolBusinessLogin: false,
      inputError: null,
      lastLoginWorkspace: getLocalStorageLastLoginWorkspace(),
      workspaceButtonAdjustedForTextLength: false, // rscdts-1444
    };

    // If the token is provided on the url, this is a SAML SSO flow
    const queryParams = parse(this.props.location.search);
    if (queryParams.rst_access_token) {
      this.state.showLogin = false;
      //console.log("Mobile detected = " + isMobileDetected);
      if (isMobileDetected){
        this.props.routeSsoFlow(queryParams);
      }
      else {
        this.props.routeWebSsoFLow(queryParams);
      }
    }
    // check for redirect query params, if provided, save in component state for use after authentication
    if (queryParams.redirect_uri) {
      // capture redirect hash before it's lost as it's not parsed into queryParams
      const location = window.location.pathname + window.location.search;
      const redirect_uri_param_name = 'redirect_uri=';
      const redirect_uri = location.slice(location.toLowerCase().indexOf(redirect_uri_param_name));
      queryParams.redirect_uri = decodeURIComponent(redirect_uri.slice(redirect_uri_param_name.length));
      this.state.redirectParams = queryParams;
    }

    // RSCDTS-1376 - totale may forward us a content path we should honor after login
    if (queryParams.content_path) {
      this.state.contentPath = queryParams.content_path;
    }
  }

  // Invoked right before calling the render method, both on the initial mount and on subsequent updates.
  static getDerivedStateFromProps = (nextProps, prevState) => {
    if ((nextProps.accountType === ACCOUNT_TYPE.CONSUMER || nextProps.accountType === ACCOUNT_TYPE.STANDALONE) && nextProps.loginStatus === LOGIN_STATUS.LOGGED_IN) {
      prevState.redirecting = true;
      let tempRedirect = nextProps.loginRedirectUrl;
      // RSCDTS-1376 - if totale passed us a content path, honor it when redirect
      if (prevState.contentPath) {
        tempRedirect = tempRedirect + "&content_path=" + prevState.contentPath;
      }
      // window.location.href = nextProps.loginRedirectUrl;
      window.location.href = tempRedirect;
    } else {
      if (nextProps.loginStatus === LOGIN_STATUS.LOGGED_IN && !prevState.redirecting && nextProps.accountType === ACCOUNT_TYPE.CATALYST) {
        if (prevState.redirectParams) {
          // user initially landed on /redirect path, now that they're logged in, proceed with the redirect
          const redirectParams = prevState.redirectParams;

          if (redirectParams.client_id && redirectParams.redirect_uri && redirectParams.response_type === 'code') {
            // convert "Product:GAIA" to "GAIA" for backward compatibility (See RSMC-13393)
            let productKey = redirectParams.client_id;
            const productString = 'Product:';
            if (productKey.indexOf(productString) >= 0) {
              productKey = productKey.slice(productKey.indexOf(productString) + productString.length);
            }

            if (launchAppWithKey(productKey, redirectParams.redirect_uri)) {
              return { redirecting: true, redirectParams: '' };
            }
            // if app fails to launch, we'll fall through to the redirect to the LP home page below
          }
        }

      }
      if (nextProps.loginStatus === LOGIN_STATUS.LOGGED_IN && !prevState.redirecting && nextProps.accountType === ACCOUNT_TYPE.CATALYST) {
        nextProps.history.push('/launchpad');
      }
    }

    return null;
  };

  componentWillUnmount() {
    // Because our error message in this component can come from Launchpad's store (errorKeyUpdated),
    // or react-auth's store (errorMsg), if we don't do this, error message related to bad logn
    // will persist as you navigate components (because they're stored in react-auth, not launchpad's errorKey)
    this.props.clearAuthError();
  }

  componentDidMount = () => {
    // Clear any error messages
    this.props.errorKeyUpdated(null);
    this.props.clearAuthError(null);
  }


  componentDidUpdate = () => {
    // I dislike that this exists, but:
    // If the string "Looking for {workspace}? sign in here" is longer than its contaier button,
    // break it into two lines. Sounds simple but it isnt.
    // We know computed button size is 388px, and there's ?px padding each side,
    // so let's make it so that if text string is longer than 375px, we break it into two lines.
    const maxWidth = '375'
    if (!this.state.workspaceButtonAdjustedForTextLength && this.state.lastLoginWorkspace && this.props.lionLoaded) {
      // Determine length of string of text. We do this by  putting it inside a span,
      // then putting the span somewhere in the DOM with css hidden. get computed size.

      // Timeout seems to be the way to go to know render has fully completed after update?
      window.setTimeout(() => {
        // This is to remove the "<br>" that comes embeded in the string from Lion.
        document.body.insertAdjacentHTML("beforeend", "<style>#workspace-hidden-span br { display: none}</style>")
        document.body.insertAdjacentHTML('afterbegin', `<span style="color: red; position: absolute; z-index:10;" id="workspace-hidden-span">${this.props.lionTranslateString('workspace_button').replace("{{workspace_fullname}}", this.state.lastLoginWorkspace)}</span>`);
        const width = document.querySelector("#workspace-hidden-span").getBoundingClientRect().width;
        if (width > maxWidth) {
          this.setState({ workspaceButtonAdjustedForTextLength: true })
        }
        // remove the span so curious visitors don't find it in the DOM by accident
        document.querySelector('#workspace-hidden-span').remove()
      },0);
    }
  }


  // If a string contains @ or . but not both, user likely wanted
  // to enter an email, but had typos. ie, me#example.com, hello@example,com
  // with this, we can show a proper "check your email" error message
  // Thanks Jessica G. for the regex !
  // Linter tells us \. is an useless escape, but it isn't.
  // eslint-disable-next-line
  isBadFormattedEmail = str => /^[^@.]*[@\.][^@.]*$/.test(str);

  emailError = () => validateUserInfo('email', this.state.tempUsername);

  handleUsernameChange = (event) => {
    this.props.errorKeyUpdated(null);
    this.props.clearAuthError(null);
    this.setState({ tempUsername: event.target.value.trim() });
  };

  // See if user tried to enter an email when they focused out of email field
  handleUsernameBlur = e => {
    if (this.isBadFormattedEmail(e.target.value)) {
      this.props.errorKeyUpdated('invalid_email');
    }
  };

  handlePasswordChange = (event) => {
    // Don't clear the 'password_reenter' error due to autofill.
    if (this.props.errorKey !== 'password_reenter') {
      this.props.errorKeyUpdated(null);
    }
    this.props.clearAuthError(null);
    this.setState({ tempPassword: event.target.value.trim() });
  };

  handleWorkspaceChange = (event) => {
    this.props.errorKeyUpdated(null);
    this.props.clearAuthError(null);
    this.setState({ tempWorkspace: event.target.value.trim() });
    // If workspace input is changed, clear the account type set by account pop-up selection
    this.setState({ tempAccountType: '' });
  };

  handleFormSubmit = e => {
    e.preventDefault();
    // Clear any existing error messages upon clicking the submit button, but prior to login attempt
    this.props.errorKeyUpdated(null);
    this.props.clearAuthError(null);
    // Remember the workspace for next time.
    if (this.state.tempWorkspace) {
      setLocalStorageLastLoginWorkspace(this.state.tempWorkspace);
    }
    // Two cases we allows the user to login:
    // 1. On the initial or S/B login page, user attempted to sign in with an email, and email is in valid format
    // 2. On the S/B, user attempted to sign in with a non-email username, and workspace is provided
    if (!this.emailError() || (this.state.showSchoolBusinessLogin && this.state.tempWorkspace !== '')) {
      this.props.consolidatedLoginUser(
        this.state.tempUsername,
        this.state.tempPassword,
        this.state.tempWorkspace,
        this.state.tempAccountType,
        this.state.rememberMeChecked,
      );
    } else {
      // User seems to be entering a email, but it's not complete
      if (this.isBadFormattedEmail(this.state.tempUsername)) {
        this.props.errorKeyUpdated('invalid_email');
      } else {  // User seems not to be entering an email
        if (this.state.showSchoolBusinessLogin) {
          this.props.errorKeyUpdated('error_workspace_required_to_login_with_username');
        } else {
          // Not a valid email, nor a badly formatted email. User entered a word/username.
          this.props.errorKeyUpdated(`please_goto_school_sign_in`);
        }
      }
    }

  };

  handleWorkspaceLoginLinkClick = (event) => {
    this.setState({ showSchoolBusinessLogin: true });
    this.setState({ tempWorkspace: this.state.lastLoginWorkspace });
  }

  handleForgotPasswordClick = (event) => {
    this.props.goToForgotPasswordNew(this.state.tempUsername);
  };

  handleSignInHelpClick = (event) => {
    const locale=getSupportLocale(this.props.currentLocale);
    window.open('https://support.rosettastone.com/s/article/I-cannot-sign-into-Rosetta-Stone-Language-Learning-Online-What-can-I-do?language='+locale, '_blank');
  };

  handleWorkspaceHelpClick = (event) => {
    const locale=getSupportLocale(this.props.currentLocale);
    window.open('https://support.rosettastone.com/s/article/What-is-my-Namespace-for-the-Rosetta-Stone-Mobile-App?language='+locale, '_blank')
  };

  // Handles click of the 'Back' link in the login box, which is only shown when in School sign in.
  handleBackButtonClick = e => {
    e.preventDefault();
    // We do this so if user attempts sign in again as consumer, app doesn't think it's catalyst
    this.props.errorKeyUpdated(null);
    this.props.clearAuthError(null);
    this.setState({ showSchoolBusinessLogin: false });
    this.setState({ tempWorkspace: '' });
    this.setState({ tempPassword: '' })
    this.setState({ tempAccountType: '' });
    this.props.setLoginStatus('');
  }

  handleSchoolOrBusinessSignInClick = (e) => {
    this.setState({ showSchoolBusinessLogin: true });
    this.props.errorKeyUpdated(null)
    this.props.clearAuthError(null);
    // Ensure remember me is always unchecked
    this.setState({ rememberMeChecked: false });
  };

  handleSsoSignInClick = (event) => {
    if (!this.props.currentLocale){
      window.location.href = '/webssologin';
    } else {
      window.location.href = '/webssologin?ui_language='+this.props.currentLocale;
    }
  };

  handleRememberMeCheckClicked = (event) => {
    this.setState({ rememberMeChecked: event.target.checked });
  };

  returnToLogin = () => {
    window.location.href = '/';
  };



  handleAccountOptionClick = (namespace, accountType) => {
    if (namespace === null) {
      this.props.consolidatedLoginUser(this.state.tempUsername, this.state.tempPassword, '', ACCOUNT_TYPE.CONSUMER, this.state.rememberMeChecked);
    } else {
      this.setState({ showSchoolBusinessLogin: true });
      this.setState({ tempWorkspace: namespace });
      this.setState({ tempPassword: '' })
      this.setState({ tempAccountType: accountType });
      // Show the "Please re-enter your password" message
      this.props.errorKeyUpdated('password_reenter');
      this.props.setLoginStatus('');
      setLocalStorageLastLoginWorkspace(namespace);
    }
  };

  getErrorMessage = () => {
    if (!this.props.errorKey) {
      return null;
    }
    if (this.props.errorKey === 'invalid_grant') {
      return this.props.lionTranslateString('login_failed_one_field_incorrect');
    }
    return this.props.lionTranslateString(this.props.errorKey);
  };

  render = () => {
    const longLanguage = ['de-DE', 'es-419', 'it-IT', 'pl-PL', 'ru-RU'].includes(this.props.currentLocale);
    const submitDisabled = this.props.errorMessage ||
      this.state.tempPassword.length < MIN_PASSWORD_LENGTH ||
      this.state.tempUsername.length === 0 ||
      this.props.loginStatus === LOGIN_STATUS.PENDING;

    if (!this.props.isAppInitialized || !this.state.showLogin || !this.props.lionLoaded || this.state.redirecting) {
      return <MouseCatcher />;
    }

    let form;

    // Show the following components if showSchoolBusinessLogin is false
    let workspaceLoginLink = null;
    if (!this.state.showSchoolBusinessLogin) {
      if (this.state.lastLoginWorkspace && this.state.lastLoginWorkspace !== '') {
        workspaceLoginLink = (
          <WorkspaceLink
            data-qa="workspaceLoginLink"
            onClick={this.handleWorkspaceLoginLinkClick}
            workspaceButtonAdjustedForTextLength={this.state.workspaceButtonAdjustedForTextLength}>
            <span dangerouslySetInnerHTML={{__html: this.props.lionTranslateString('workspace_button').replace("{{workspace_fullname}}", this.state.lastLoginWorkspace)}} />
            <svg width="8" height="8" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.40999 0L0 1.41L4.57998 6L0 10.59L1.40999 12L7.40997 6L1.40999 0Z" fill="#02B1FF"/>
            </svg>
          </WorkspaceLink>
        );
      }
    }

    // Show the following components if showSchoolBusinessLogin is true
    const rememberMeCheckbox = (
      <RememberMeCheckboxContainer>
        <input
          type="checkbox"
          id="RememberMeCheckbox"
          data-qa="RememberMeCheckbox"
          defaultChecked={this.state.rememberMeChecked}
          onClick={this.handleRememberMeCheckClicked}
          tabIndex={3}
        />
        <RememberMeLabel
          htmlFor="RememberMeCheckbox"
          data-qa="RememberMeLabel"
          dangerouslySetInnerHTML={{ __html: this.props.lionTranslateString('remember') }}
        />
        <HelpIcon tipText={this.props.lionTranslateString('tooltip_remember_me')}
          tippedElement={this}
          tipPosition={"top"}
          helpTextKey={"test"}
        />
      </RememberMeCheckboxContainer>
    );

    const  workspaceHelpLink = (
      <Link data-qa="WorkspaceHelpLink" onClick={this.handleWorkspaceHelpClick} textAlign={"left"}>
        { this.props.lionTranslateString('what_is_my_workspace')}
      </Link>
    );

    // This is the pop-up that appears when the user has multiple accounts
    if (this.props.loginStatus === LOGIN_STATUS.ACCOUNT_CHOOSE) {
      // Build array of multiple account buttons, if user has them
      let accountButtons = this.props.accountList.map((account, index) =>
        <Link key={index}
          data-qa={`AccountOption-${index}`}
          onClick={() => this.handleAccountOptionClick(account.namespace, account.account_type)}
          tabIndex={1}
        >
          {account.namespace !== null ? account.namespace : this.props.lionTranslateString('personal')}
        </Link>
      );

      form = <Form>
          <P>
            {this.props.lionTranslateString('multiple_accounts_found')}
          </P>
          <MultipleAccountsContainer>
            {accountButtons}
          </MultipleAccountsContainer>
          <Link data-qa="PopUpCancelLink" onClick={() => this.props.logoutUser() }>
            {this.props.lionTranslateString('forgotpassword_cancel_link')}
          </Link>
        </Form>
    } else {
      form = <Form onSubmit={this.handleFormSubmit} autoComplete="on">
        { workspaceLoginLink }
        
        <TextInput
          type="text"
          dataQa="Email"
          placeholder={this.state.showSchoolBusinessLogin ? this.props.lionTranslateString('login_username_field') : this.props.lionTranslateString('login_email_field')}
          value={this.state.tempUsername}
          autoFocus={true}
          onChange={this.handleUsernameChange}
          onBlur={this.handleUsernameBlur}
          tabIndex={1}
          name="email"
          autoComplete='email'
        />

        {/* Dummy input fields to fix bug: https://ixl-learning.atlassian.net/browse/RSCDTS-1490 */}
        <div style={{display: "none"}}>
          <input type="text" value="" readOnly />
          <input type="text" value="" readOnly />
        </div>
        
        <TextInput
          type="password"
          dataQa="Password"
          placeholder={this.props.lionTranslateString('login_password_field')}
          tabIndex={2}
          value={this.state.tempPassword}
          onChange={this.handlePasswordChange}
          name="password"
          autoComplete='current-password'
        />
        { this.state.showSchoolBusinessLogin &&
        <React.Fragment>
          <TextInput
            inputType="text"
            dataQa="Workspace"
            placeholder={this.props.lionTranslateString('login_workspace_field')}
            tabIndex={3}
            value={this.state.tempWorkspace}
            onChange={this.handleWorkspaceChange}
          />
          { workspaceHelpLink }
        </React.Fragment>
        }
        <LaunchpadButtonNew data-qa="SignInButton" disabled={submitDisabled} tabIndex={3}>
          {this.props.lionTranslateString('login_button_signin')}
        </LaunchpadButtonNew>
        { !this.state.showSchoolBusinessLogin && rememberMeCheckbox }


      </Form>
    }

    // Display "School Sign In" or "Back" at the bottom, depending which view the user is in
    const alternteLoginLink = this.state.showSchoolBusinessLogin ?
      <Link
        data-qa="SchoolOrBusinessSignIn"
        dangerouslySetInnerHTML={{__html: this.props.lionTranslateString('back')}}
        longLanguage={longLanguage}
        onClick={(e) => this.handleBackButtonClick(e)}
      />
      :
      <Link
        data-qa="SchoolOrBusinessSignIn"
        dangerouslySetInnerHTML={{__html: this.props.lionTranslateString('school_sign_in')}}
        longLanguage={longLanguage}
        onClick={(e) => this.handleSchoolOrBusinessSignInClick(e)}
      />

    // These are the links at the footer of login container.
    let signInHelpLinks = (<SignInHelpContainer longLanguage={longLanguage} showSchoolBusinessLogin={this.state.showSchoolBusinessLogin}>
      <div>
        { alternteLoginLink }
        <Link
          data-qa="SSOSignInButton"
          dangerouslySetInnerHTML={{__html: this.props.lionTranslateString('sso_sign_in_link')}}
          longLanguage={longLanguage}
          onClick={this.handleSsoSignInClick}
        />
      </div>
      <span>{/* just a spacer */ }</span>
      <div>
        <Link data-qa="SignInHelpLink" longLanguage={longLanguage} onClick={this.handleSignInHelpClick}>
          {this.props.lionTranslateString('sign_in_help')}
        </Link>
        <Link data-qa="ForgotPasswordLink" longLanguage={longLanguage} onClick={this.handleForgotPasswordClick}>
          { this.props.lionTranslateString('login_forgot_password_link') }
        </Link>
      </div>
    </SignInHelpContainer>)

    // If we're in account choose view, we don't want to show those links at the bottom
    if (this.props.loginStatus === LOGIN_STATUS.ACCOUNT_CHOOSE){
      signInHelpLinks = null;
    }
    return  (
      <LoginDiv
        header={this.props.lionTranslateString('welcome')}
        signInHelpLinks={signInHelpLinks}
        errorMessage={this.getErrorMessage()}>
        { form }
      </LoginDiv>
    );
  };
}

const mapStateToProps = (state, ownprops) => ({
  isAppInitialized: appSelectors.isAppInitialized(state),
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  currentLocale: lionSelectors.getLionLanguage(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  },
  loginStatus: authSelectors.loginStatus(state),
  accountType: authSelectors.accountType(state),
  accountList: authSelectors.accountList(state),
  loginRedirectUrl: authSelectors.loginRedirectUrl(state),
  errorKey: appSelectors.errorKey(state) || authSelectors.errorMsg(state),
});

const wrapActions = {
  loginUser: appActions.loginUser,
  logoutUser: appActions.logoutUser,
  consolidatedLoginUser: appActions.consolidatedLoginUser,
  setLoginStatus: authActions.setLoginStatus,
  routeSsoFlow: ssoLoginActions.routeSsoFlow,
  routeWebSsoFLow: ssoLoginActions.routeWebSsoFlow,
  goToForgotPasswordNew: passwordChangeActions.goToForgotPasswordNew,
  errorKeyUpdated: appActions.errorKeyUpdated,
  clearAuthError: authActions.clearAuthError,
};

export const LoginContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(Login));



// WEBPACK FOOTER //
// ./src/modules/login/components/Login.jsx